import { useState, useRef, useMemo, useCallback, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  Plus, Check, GripVertical, Trash2, X, ChevronDown, ChevronRight, Calendar,
  ListTodo as ListIcon, Settings2, MessageSquare,
} from 'lucide-react';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import type { DragEndEvent } from '@dnd-kit/core';
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
  arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  useMyTodos,
  useCreateTodo,
  useUpdateTodo,
  useDeleteTodo,
  useReorderTodos,
} from '../../hooks/useMyHome';
import type { TodoItem, UpdateTodoInput } from '../../hooks/useMyHome';
import Spinner from '../ui/Spinner';
import TodoCalendarView from './TodoCalendarView';
import SourceMessageMeta from '../chat/SourceMessageMeta';

// ── 우선순위 색상 ──
const PRIORITY_DOT: Record<string, string> = {
  HIGH: 'bg-red-500',
  MEDIUM: 'bg-yellow-500',
  LOW: 'bg-blue-500',
};

const PRIORITY_OPTIONS = [
  { value: 'HIGH' as const, label: '높음', color: 'text-red-600' },
  { value: 'MEDIUM' as const, label: '보통', color: 'text-yellow-600' },
  { value: 'LOW' as const, label: '낮음', color: 'text-blue-600' },
];

// ── 완료 항목 표시 설정 ──
const COMPLETED_SETTINGS_KEY = 'myHome:todoCompletedSettings';

type CompletedPeriod = '1w' | '1m' | '3m';

interface CompletedSettings {
  hideCompleted: boolean;
  period: CompletedPeriod;
}

const PERIOD_LABELS: Record<CompletedPeriod, string> = {
  '1w': '1주',
  '1m': '1개월',
  '3m': '3개월',
};

const PERIOD_DAYS: Record<CompletedPeriod, number> = {
  '1w': 7,
  '1m': 30,
  '3m': 90,
};

function loadCompletedSettings(): CompletedSettings {
  try {
    const raw = localStorage.getItem(COMPLETED_SETTINGS_KEY);
    if (raw) return JSON.parse(raw);
  } catch { /* ignore */ }
  return { hideCompleted: false, period: '1w' };
}

function saveCompletedSettings(s: CompletedSettings) {
  localStorage.setItem(COMPLETED_SETTINGS_KEY, JSON.stringify(s));
}

// ── 날짜 그룹 헬퍼 ──
const WEEKDAYS = ['일', '월', '화', '수', '목', '금', '토'];

function getDateGroup(dueDate: string | null): { key: string; label: string; sortVal: number } {
  if (!dueDate) return { key: 'no-date', label: '기한 없음', sortVal: Infinity };
  const now = new Date();
  const d = new Date(dueDate);
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const target = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const diff = (target.getTime() - today.getTime()) / 86_400_000;

  const label =
    diff === 0 ? '오늘' :
    diff === 1 ? '내일' :
    `${d.getMonth() + 1}/${d.getDate()} (${WEEKDAYS[d.getDay()]})`;

  return { key: dueDate.slice(0, 10), label, sortVal: target.getTime() };
}

function formatTime(dueTime: string | null): string {
  if (!dueTime) return '';
  const d = new Date(dueTime);
  if (isNaN(d.getTime())) return '';
  return `${String(d.getUTCHours()).padStart(2, '0')}:${String(d.getUTCMinutes()).padStart(2, '0')}`;
}

// ── 정렬 가능한 To-do 항목 ──
function SortableTodoRow({
  todo,
  onToggle,
  onEdit,
  onJumpToSourceMessage,
  isHighlighted,
}: {
  todo: TodoItem;
  onToggle: (id: string, completed: boolean) => void;
  onEdit: (todo: TodoItem) => void;
  onJumpToSourceMessage: (todo: TodoItem) => void;
  isHighlighted: boolean;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: todo.id,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const issueKey = todo.linkedIssue?.issueId;
  const projectKey = issueKey?.split('-')[0];
  const isOverdue = !todo.completed && !!todo.dueDate && new Date(todo.dueDate) < new Date(new Date().toDateString());

  const sourceChannelName = todo.sourceMessage?.channel?.name ?? null;

  return (
    <div
      ref={setNodeRef}
      data-todo-id={todo.id}
      style={style}
      className={`flex items-center gap-1.5 px-2 py-1.5 rounded-md group transition-colors ${
        isHighlighted ? 'bg-yellow-100 ring-2 ring-yellow-400' :
        todo.completed ? 'opacity-60 hover:bg-gray-50' : isOverdue ? 'bg-red-50/60 hover:bg-red-50' : 'hover:bg-gray-50'
      }`}
    >
      {/* 드래그 핸들 */}
      <button
        type="button"
        className="cursor-grab text-gray-300 hover:text-gray-500 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity"
        {...attributes}
        {...listeners}
      >
        <GripVertical className="size-3.5" />
      </button>

      {/* 체크박스 */}
      <button
        type="button"
        onClick={() => onToggle(todo.id, !todo.completed)}
        className={`size-4 rounded border flex items-center justify-center shrink-0 transition-colors ${
          todo.completed
            ? 'bg-primary-500 border-primary-500 text-white'
            : 'border-gray-300 hover:border-primary-400'
        }`}
      >
        {todo.completed && <Check className="size-3" />}
      </button>

      {/* 내용 */}
      <button
        type="button"
        onClick={() => onEdit(todo)}
        className="flex-1 min-w-0 text-left"
      >
        <span className={`text-sm leading-snug ${
          todo.completed ? 'line-through text-gray-400' : isOverdue ? 'text-red-600' : 'text-gray-700'
        }`}>
          {todo.title}
        </span>
      </button>

      {/* 이슈 키 뱃지 */}
      {issueKey && projectKey && (
        <Link
          to={`/projects/${projectKey}/issues`}
          onClick={(e) => e.stopPropagation()}
          className="text-[11px] font-medium text-primary-600 bg-primary-50 px-1 py-0.5 rounded shrink-0 hover:bg-primary-100"
        >
          {issueKey}
        </Link>
      )}

      {/* 시간 */}
      {todo.dueTime && (
        <span className="text-[11px] text-gray-400 shrink-0">{formatTime(todo.dueTime)}</span>
      )}

      {/* CHG-123 REQ-V123-041: 메시지 출처 아이콘 + 클릭 라우팅 */}
      {todo.sourceMessageId && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onJumpToSourceMessage(todo); }}
          className="text-gray-300 hover:text-primary-600 shrink-0"
          title={sourceChannelName ? `${sourceChannelName}의 메시지에서 변환됨` : '메시지에서 변환됨'}
          aria-label="원본 메시지로 이동"
        >
          <MessageSquare className="size-3" />
        </button>
      )}

      {/* 우선순위 점 */}
      <span className={`size-2 rounded-full shrink-0 ${PRIORITY_DOT[todo.priority]}`} />
    </div>
  );
}

// ── 상세 편집 모달 ──
function EditModal({
  todo,
  onClose,
  onSave,
  onDelete,
}: {
  todo: TodoItem;
  onClose: () => void;
  onSave: (id: string, input: UpdateTodoInput) => void;
  onDelete: (id: string) => void;
}) {
  const [title, setTitle] = useState(todo.title);
  const [priority, setPriority] = useState(todo.priority);
  const [dueDate, setDueDate] = useState(todo.dueDate?.slice(0, 10) ?? '');
  const [dueTime, setDueTime] = useState(todo.dueTime ? formatTime(todo.dueTime) : '');
  const [description, setDescription] = useState(todo.description ?? '');

  const isOverdue = !!dueDate && !todo.completed && new Date(dueDate) < new Date(new Date().toDateString());

  const handleSave = () => {
    onSave(todo.id, {
      title: title.trim() || todo.title,
      priority,
      dueDate: dueDate || null,
      dueTime: dueTime || null,
      description: description || null,
    });
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl w-96 max-w-[90vw] p-5 space-y-4" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold text-gray-900">할 일 편집</h3>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="size-4" />
          </button>
        </div>

        {/* 제목 */}
        <input
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-200"
          placeholder="제목"
        />

        {/* 우선순위 */}
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-500 w-14">우선순위</span>
          <div className="flex gap-1">
            {PRIORITY_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                type="button"
                onClick={() => setPriority(opt.value)}
                className={`px-2 py-1 text-sm rounded-md border transition-colors ${
                  priority === opt.value
                    ? `${opt.color} border-current bg-opacity-10 font-medium`
                    : 'text-gray-400 border-gray-200 hover:border-gray-300'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        {/* 기한 + 시간 */}
        <div>
          <div className="flex items-center gap-2 mb-1.5">
            <span className="text-sm text-gray-500 w-14 shrink-0">기한</span>
            <input
              type="date"
              value={dueDate}
              onChange={(e) => setDueDate(e.target.value)}
              className={`flex-1 min-w-0 px-2 py-1.5 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-200 ${
                isOverdue ? 'border-red-300 text-red-600' : 'border-gray-200'
              }`}
            />
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-500 w-14 shrink-0">시간</span>
            <input
              type="time"
              value={dueTime}
              onChange={(e) => setDueTime(e.target.value)}
              className="flex-1 min-w-0 px-2 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-200"
            />
          </div>
          {isOverdue && (
            <p className="text-[11px] text-red-500 mt-1 ml-16">기한이 지났습니다</p>
          )}
        </div>

        {/* 메모 */}
        <div>
          <span className="text-sm text-gray-500">메모</span>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={3}
            className="w-full mt-1 px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-200 resize-none"
            placeholder="메모를 입력하세요"
          />
        </div>

        {/* 이슈 연결 표시 */}
        {todo.linkedIssue && (
          <div className="flex items-center gap-1 text-sm text-gray-500">
            <span>연결된 이슈:</span>
            <span className="font-medium text-primary-600">{todo.linkedIssue.issueId}</span>
            <span className="truncate">{todo.linkedIssue.title}</span>
          </div>
        )}

        {/* CHG-123 REQ-V123-052: 채팅 메시지 출처 */}
        {todo.sourceMessage && (
          <div className="pt-1 border-t border-gray-100">
            <SourceMessageMeta
              sourceMessage={{
                messageId: todo.sourceMessage.messageId,
                isDeleted: todo.sourceMessage.isDeleted,
                createdAt: todo.createdAt,
                channel: todo.sourceMessage.channel
                  ? {
                      id: todo.sourceMessage.channel.id,
                      name: todo.sourceMessage.channel.name,
                      type: todo.sourceMessage.channel.type,
                      isArchived: todo.sourceMessage.channel.isArchived,
                      projectKey: todo.sourceMessage.channel.projectKey,
                    }
                  : null,
              }}
            />
          </div>
        )}

        {/* 버튼 */}
        <div className="flex items-center justify-between pt-2">
          <button
            type="button"
            onClick={() => { onDelete(todo.id); onClose(); }}
            className="flex items-center gap-1 px-3 py-1.5 text-sm text-red-600 hover:bg-red-50 rounded-md transition-colors"
          >
            <Trash2 className="size-3" /> 삭제
          </button>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={onClose}
              className="px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100 rounded-md transition-colors"
            >
              취소
            </button>
            <button
              type="button"
              onClick={handleSave}
              className="px-3 py-1.5 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-md transition-colors"
            >
              저장
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── 메인 컴포넌트 ──
export default function TodoSidePanel() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [tab, setTab] = useState<'list' | 'calendar'>('list');
  const [showCompleted, setShowCompleted] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [completedSettings, setCompletedSettings] = useState<CompletedSettings>(loadCompletedSettings);
  const [addMode, setAddMode] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [editingTodo, setEditingTodo] = useState<TodoItem | null>(null);
  // CHG-123 REQ-V123-040: 마이 홈 도착 시 focusTodo 하이라이트
  const [highlightedTodoId, setHighlightedTodoId] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const settingsRef = useRef<HTMLDivElement>(null);

  const { data: todos, isLoading } = useMyTodos();
  const createTodo = useCreateTodo();
  const updateTodo = useUpdateTodo();
  const deleteTodo = useDeleteTodo();
  const reorderTodos = useReorderTodos();

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  // 설정 변경 핸들러
  const updateSettings = useCallback((patch: Partial<CompletedSettings>) => {
    setCompletedSettings((prev) => {
      const next = { ...prev, ...patch };
      saveCompletedSettings(next);
      return next;
    });
  }, []);

  // 설정 드롭다운 외부 클릭 닫기
  useEffect(() => {
    if (!showSettings) return;
    const handle = (e: MouseEvent) => {
      if (settingsRef.current && !settingsRef.current.contains(e.target as Node)) setShowSettings(false);
    };
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, [showSettings]);

  // 미완료 / 완료 분리 (기간 필터 적용)
  const { pending, completed } = useMemo(() => {
    if (!todos) return { pending: [], completed: [] };
    const now = Date.now();
    const cutoff = now - PERIOD_DAYS[completedSettings.period] * 86_400_000;
    return {
      pending: todos.filter((t) => !t.completed),
      completed: todos
        .filter((t) => t.completed)
        .filter((t) => new Date(t.updatedAt).getTime() >= cutoff),
    };
  }, [todos, completedSettings.period]);

  // 날짜 그룹핑 (일별)
  const grouped = useMemo(() => {
    const groups = new Map<string, { label: string; sortVal: number; items: TodoItem[] }>();
    for (const t of pending) {
      const { key, label, sortVal } = getDateGroup(t.dueDate);
      if (!groups.has(key)) groups.set(key, { label, sortVal, items: [] });
      groups.get(key)!.items.push(t);
    }
    return [...groups.values()].sort((a, b) => a.sortVal - b.sortVal);
  }, [pending]);

  const allPendingIds = useMemo(() => pending.map((t) => t.id), [pending]);

  // 핸들러
  const handleCreate = useCallback(() => {
    if (!newTitle.trim() || createTodo.isPending) return;
    createTodo.mutate({ title: newTitle.trim() });
    setNewTitle('');
  }, [newTitle, createTodo]);

  const handleToggle = useCallback(
    (id: string, completed: boolean) => {
      updateTodo.mutate({ todoId: id, completed });
    },
    [updateTodo],
  );

  const handleSave = useCallback(
    (id: string, input: UpdateTodoInput) => {
      updateTodo.mutate({ todoId: id, ...input });
    },
    [updateTodo],
  );

  const handleDelete = useCallback(
    (id: string) => {
      deleteTodo.mutate(id);
    },
    [deleteTodo],
  );

  // CHG-123 REQ-V123-042: 출처 메시지로 이동
  const handleJumpToSourceMessage = useCallback((todo: TodoItem) => {
    const src = todo.sourceMessage;
    if (!src) return;
    if (src.isDeleted || !src.channel) {
      toast('원본 메시지가 삭제되었습니다.');
      return;
    }
    const ch = src.channel;
    if (ch.type === 'PROJECT' && ch.projectKey) {
      navigate(`/projects/${ch.projectKey}/chat?messageId=${src.messageId}`);
    } else {
      navigate(`/chat/${ch.id}?messageId=${src.messageId}`);
    }
  }, [navigate]);

  // CHG-123 REQ-V123-040: focusTodo 쿼리 파라미터 처리 (스크롤 + 2초 하이라이트)
  useEffect(() => {
    const focusTodo = searchParams.get('focusTodo');
    if (!focusTodo || !todos) return;
    const exists = todos.some((t) => t.id === focusTodo);
    if (!exists) return;
    setHighlightedTodoId(focusTodo);
    requestAnimationFrame(() => {
      const el = document.querySelector(`[data-todo-id="${focusTodo}"]`);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
    const timer = setTimeout(() => {
      setHighlightedTodoId(null);
      setSearchParams((prev) => { const next = new URLSearchParams(prev); next.delete('focusTodo'); return next; }, { replace: true });
    }, 2000);
    return () => clearTimeout(timer);
  }, [searchParams, todos, setSearchParams]);

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;

      const oldIndex = allPendingIds.indexOf(active.id as string);
      const newIndex = allPendingIds.indexOf(over.id as string);
      if (oldIndex === -1 || newIndex === -1) return;

      const newOrder = arrayMove(allPendingIds, oldIndex, newIndex);
      reorderTodos.mutate(newOrder);
    },
    [allPendingIds, reorderTodos],
  );

  return (
    <div>
      {/* 헤더 */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex bg-gray-100 rounded-lg p-0.5">
          <button
            type="button"
            onClick={() => setTab('list')}
            className={`flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
              tab === 'list' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <ListIcon className="size-3.5" /> 리스트
          </button>
          <button
            type="button"
            onClick={() => setTab('calendar')}
            className={`flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
              tab === 'calendar' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <Calendar className="size-3.5" /> 캘린더
          </button>
        </div>
        <div className="flex items-center gap-1">
          <div className="relative" ref={settingsRef}>
            <button
              type="button"
              onClick={() => setShowSettings((v) => !v)}
              className={`p-1 rounded-md transition-colors ${showSettings ? 'bg-gray-100 text-gray-600' : 'text-gray-400 hover:text-gray-600'}`}
              title="완료 항목 설정"
            >
              <Settings2 className="size-3.5" />
            </button>
            {showSettings && (
              <div className="absolute right-0 top-full mt-1 w-52 bg-white rounded-lg shadow-lg border border-gray-200 p-3 z-30">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-gray-700">완료 항목 숨기기</span>
                  <button
                    type="button"
                    onClick={() => updateSettings({ hideCompleted: !completedSettings.hideCompleted })}
                    className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${completedSettings.hideCompleted ? 'bg-primary-500' : 'bg-gray-300'}`}
                  >
                    <span className={`inline-block size-3.5 rounded-full bg-white shadow-sm transition-transform ${completedSettings.hideCompleted ? 'translate-x-[18px]' : 'translate-x-[3px]'}`} />
                  </button>
                </div>
                <div>
                  <span className="text-[11px] text-gray-500">완료 항목 표시 기간</span>
                  <div className="flex gap-1 mt-1">
                    {(['1w', '1m', '3m'] as CompletedPeriod[]).map((p) => (
                      <button
                        key={p}
                        type="button"
                        onClick={() => updateSettings({ period: p })}
                        className={`flex-1 px-2 py-1 text-[11px] rounded-md border transition-colors ${
                          completedSettings.period === p
                            ? 'border-primary-300 bg-primary-50 text-primary-700 font-medium'
                            : 'border-gray-200 text-gray-400 hover:border-gray-300'
                        }`}
                      >
                        {PERIOD_LABELS[p]}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
          <button
            type="button"
            onClick={() => { setAddMode(true); setTimeout(() => inputRef.current?.focus(), 50); }}
            className="flex items-center gap-0.5 px-2 py-1 text-sm font-medium text-primary-600 hover:bg-primary-50 rounded-md transition-colors"
          >
            <Plus className="size-3.5" /> 추가
          </button>
        </div>
      </div>

      {tab === 'calendar' ? (
        <TodoCalendarView />
      ) : (
        <>
          {/* 빠른 추가 */}
          {addMode && (
            <div className="flex items-center gap-1.5 px-2 py-1.5 mb-2 bg-gray-50 rounded-md">
              <div className="size-4 rounded border border-gray-300 shrink-0" />
              <input
                ref={inputRef}
                type="text"
                value={newTitle}
                onChange={(e) => setNewTitle(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.nativeEvent.isComposing) handleCreate();
                  if (e.key === 'Escape') { setAddMode(false); setNewTitle(''); }
                }}
                className="flex-1 bg-transparent text-sm text-gray-700 focus:outline-none placeholder:text-gray-400"
                placeholder="할 일을 입력 후 Enter"
              />
              <button
                type="button"
                onClick={() => { setAddMode(false); setNewTitle(''); }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="size-3.5" />
              </button>
            </div>
          )}

          {isLoading ? (
            <Spinner />
          ) : (
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
              <SortableContext items={allPendingIds} strategy={verticalListSortingStrategy}>
                {grouped.length === 0 && !addMode && (
                  <p className="py-6 text-center text-sm text-gray-400">할 일이 없습니다</p>
                )}
                {grouped.map(({ label, sortVal, items }) => {
                  const isPastGroup = sortVal !== Infinity && sortVal < new Date(new Date().toDateString()).getTime();
                  return (
                  <div key={sortVal} className="mb-3">
                    <p className={`text-[11px] font-semibold uppercase tracking-wider px-2 mb-1 ${isPastGroup ? 'text-red-400' : 'text-gray-400'}`}>
                      {label}
                    </p>
                    {items.map((todo) => (
                      <SortableTodoRow
                        key={todo.id}
                        todo={todo}
                        onToggle={handleToggle}
                        onEdit={setEditingTodo}
                        onJumpToSourceMessage={handleJumpToSourceMessage}
                        isHighlighted={highlightedTodoId === todo.id}
                      />
                    ))}
                  </div>
                  );
                })}
              </SortableContext>
            </DndContext>
          )}

          {/* 완료된 항목 */}
          {completed.length > 0 && !completedSettings.hideCompleted && (
            <div className="mt-3 border-t border-gray-100 pt-2">
              <button
                type="button"
                onClick={() => setShowCompleted((v) => !v)}
                className="flex items-center gap-1 px-2 py-1 text-[11px] text-gray-400 hover:text-gray-600"
              >
                {showCompleted ? <ChevronDown className="size-3" /> : <ChevronRight className="size-3" />}
                완료된 항목 ({completed.length})
              </button>
              {showCompleted && (
                <div className="mt-1">
                  {completed.map((todo) => (
                    <div
                      key={todo.id}
                      className="flex items-center gap-1.5 px-2 py-1.5 group"
                    >
                      <button
                        type="button"
                        onClick={() => handleToggle(todo.id, false)}
                        className="size-4 rounded border bg-primary-500 border-primary-500 text-white flex items-center justify-center shrink-0"
                      >
                        <Check className="size-3" />
                      </button>
                      <span className="text-sm text-gray-400 line-through flex-1 truncate">{todo.title}</span>
                      <button
                        type="button"
                        onClick={() => handleDelete(todo.id)}
                        className="text-gray-300 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity shrink-0"
                      >
                        <Trash2 className="size-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </>
      )}

      {/* 편집 모달 */}
      {editingTodo && (
        <EditModal
          todo={editingTodo}
          onClose={() => setEditingTodo(null)}
          onSave={handleSave}
          onDelete={handleDelete}
        />
      )}
    </div>
  );
}
